import { oc } from "@orpc/contract";
import { z } from "zod";

// ─── Shared schemas ─────────────────────────────────────────────────────────

export const ProductMarginSalesByYearSchema = z.object({
  year: z.number().int(),
  /** Total quantity sold across all line items for this product in this year. */
  qtySold: z.number().int(),
  /** Quantity-weighted average sales price in USD (decimal string). */
  aspWeighted: z.string(),
});

export const ProductMarginRowSchema = z.object({
  productVariantId: z.string().uuid(),
  sku: z.string().nullable(),
  productName: z.string(),
  description: z.string().nullable(),
  /** Cost in USD as a decimal string (post-conversion if costCurrency is EUR). */
  costUsd: z.string().nullable(),
  /** Raw cost as stored in `origin_metadata.FROZEN_ITEM_COST`, decimal string. */
  costRaw: z.string().nullable(),
  /** The currency the raw cost is recorded in for the source org. */
  costCurrency: z.enum(["USD", "EUR"]),
  /** Target margin percent (e.g. "71.5" for 71.5%), decimal string. */
  targetMarginPct: z.string().nullable(),
  productFamily: z.string().nullable(),
  productGroup: z.string().nullable(),
  productModel: z.string().nullable(),
  /** Historical sales per year (descending by year). Empty when the product
   *  has no sales history in the queried window. */
  salesByYear: z.array(ProductMarginSalesByYearSchema),
});

const MetaSchema = {
  hasProductMargins: z.boolean(),
  costCurrency: z.enum(["USD", "EUR"]),
  conversionApplied: z.boolean(),
  /** Years included in this query's sales aggregation, descending.
   *  Frontend uses this to label year columns dynamically. */
  yearsCovered: z.array(z.number().int()),
} as const;

// ─── list ───────────────────────────────────────────────────────────────────

export const ListProductMarginsInputSchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  search: z.string().trim().optional(),
  family: z.string().trim().optional(),
  group: z.string().trim().optional(),
  model: z.string().trim().optional(),
});

export const ListProductMarginsResponseSchema = z.object({
  rows: z.array(ProductMarginRowSchema),
  total: z.number().int().min(0),
  ...MetaSchema,
});

// ─── byVariantIds ───────────────────────────────────────────────────────────

export const ProductMarginsByVariantIdsInputSchema = z.object({
  productVariantIds: z.array(z.string().uuid()).max(500),
});

export const ProductMarginsByVariantIdsResponseSchema = z.object({
  rows: z.array(ProductMarginRowSchema),
  ...MetaSchema,
});

// ─── classifications ───────────────────────────────────────────────────────

export const ProductMarginClassificationSchema = z.object({
  family: z.string(),
  group: z.string(),
  model: z.string(),
});

export const ProductMarginClassificationsResponseSchema = z.object({
  classifications: z.array(ProductMarginClassificationSchema),
});

// ─── Contract ───────────────────────────────────────────────────────────────

export const productMarginsContracts = {
  list: oc
    .input(ListProductMarginsInputSchema)
    .output(ListProductMarginsResponseSchema)
    .route({
      method: "GET",
      summary: "List product margins (paginated) for the current organization",
    }),
  byVariantIds: oc
    .input(ProductMarginsByVariantIdsInputSchema)
    .output(ProductMarginsByVariantIdsResponseSchema)
    .route({
      method: "POST",
      summary: "Bulk-load product margin rows by product variant id",
    }),
  classifications: oc.output(ProductMarginClassificationsResponseSchema).route({
    method: "GET",
    summary:
      "Distinct PRODVIEW (family, group, model) triples for the current org's products",
  }),
} as const;

// ─── Type exports ───────────────────────────────────────────────────────────

export type ProductMarginRow = z.infer<typeof ProductMarginRowSchema>;
export type ListProductMarginsInput = z.infer<
  typeof ListProductMarginsInputSchema
>;
export type ListProductMarginsResponse = z.infer<
  typeof ListProductMarginsResponseSchema
>;
export type ProductMarginsByVariantIdsInput = z.infer<
  typeof ProductMarginsByVariantIdsInputSchema
>;
export type ProductMarginsByVariantIdsResponse = z.infer<
  typeof ProductMarginsByVariantIdsResponseSchema
>;
export type ProductMarginClassification = z.infer<
  typeof ProductMarginClassificationSchema
>;
export type ProductMarginClassificationsResponse = z.infer<
  typeof ProductMarginClassificationsResponseSchema
>;
export type ProductMarginSalesByYear = z.infer<
  typeof ProductMarginSalesByYearSchema
>;
